import * as types from '@/store/mutation-types'
import { Repository } from '@/repository/repository'
import { services } from '@/services'
import { stripPayload } from '@/schemas'
import config from '../../../config'

import { datadogRum as datadog } from '@datadog/browser-rum'

const apiRepository = Repository.call('user')

const _setUserToLogs = ({ id, login, email }) => {
  if (config.datadog.enable) {
    datadog.setUser({
      id,
      name: login,
      email
    })
  }
}

const state = {
  data: {},
  registrations: [],
  partners: [],
  profiles: [],
  stores: [],
  drivers: []
}

const getters = {
  authorization: state => state.data.auth,
  getUserName: state => state.data.email,
  registrations: state => state.registrations,
  partners: state => state.partners,
  drivers: state => state.drivers,
  stores: state => state.stores,
  profiles: state => state.profiles,
  status: state => state.data
}

const actions = {
  async setAuthorization ({ commit }, userData) {
    const response = await apiRepository.getAuth({
      email: userData.email,
      password: userData.password,
      rememberMe: true
    })
    if (!response.level) return response

    const data = {
      auth: response.headers.authorization,
      email: userData.email
    }
    commit(types.SET_USER_AUTH_HEADER, data)

    const user = await apiRepository.getUserData()
    if (!user.level) return user

    const userPayload = { ...user.data }
    commit(types.SET_DATA_USER, userPayload)

    if (process.env.NODE_ENV !== 'development') _setUserToLogs(userPayload)

    const setUser = services.userService(response.data)
    if (!setUser.login()) return false
    return response
  },

  async setRegistration ({ commit }, data) {
    const {
      id,
      registration
    } = data

    const operator = {
      id,
      status: 'ACTIVE'
    }

    const params = {
      operator,
      registration,
      registrationId: data.registrationId || false,
      status: data.status || false
    }

    const response = await apiRepository.setRegistration(params)
    return response
  },

  async setDriver ({ commit }, data) {
    const response = await apiRepository.setDriver(data)
    if (!response.level) return response

    const {
      fullName,
      driversHadDisassociatedVehicle
    } = response.data

    const lastDriver = driversHadDisassociatedVehicle[0] ? driversHadDisassociatedVehicle[0].fullName : ''

    response.driverName = fullName.toUpperCase()
    response.lastDriver = lastDriver.toUpperCase()
    return response
  },

  async setPassword ({ commit }, data) {
    const response = await apiRepository.setPassword(data)
    return response
  },

  async setNewRequestPassword ({ commit }, mail) {
    const response = await apiRepository.setNewRequestPassword({ mail })
    return response
  },

  async getVehicles ({ commit }) {
    const response = await apiRepository.getVehicles()
    if (!response.level) return response

    const registrations = services.cleanResponseService(stripPayload(response.data, 'vehicles'))
    commit(types.SET_USER_REGISTRATIONS, registrations)
  },

  async getDrivers ({ commit }) {
    const response = await apiRepository.getDrivers()
    if (!response.level) return response

    const drivers = services.cleanResponseService(stripPayload(response.data, 'drivers'))
    commit(types.SET_USER_DRIVERS, drivers)
  },

  async deleteRegistration ({ commit }, registrations) {
    let registration = registrations.map(r => apiRepository.deleteVehicles(r.id))
    const response = await Promise.all(registration)

    return response[0]
  },

  async getPartners ({ commit }) {
    const response = await apiRepository.getPartners()
    if (!response.level) return response

    const partners = services.cleanResponseService(stripPayload(response.data, 'partners'))
    commit(types.SET_USER_PARTNERS, partners)
  },

  async getProfiles ({ commit }) {
    const response = await apiRepository.getProfiles()
    if (!response.level) return response

    const cleanResponse = services.cleanResponseService(response.data)
    commit(types.SET_USER_PROFILES, cleanResponse)
  },

  async getStores ({ commit }) {
    const response = await apiRepository.getStores()
    if (!response.level) return response

    const stores = services.cleanResponseService(stripPayload(response.data, 'stores'))
    stores.sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase()))
    commit(types.SET_STORES, stores)
  },

  async setNewUser ({ commit }, body) {
    const response = await apiRepository.setNewUser(body)
    return response
  },

  flush ({ commit }) {
    commit(types.SET_FLUSH_USER)
  }
}

const mutations = {
  [types.SET_USER_AUTH_HEADER] (state, data) {
    state.data = { ...data }
  },
  [types.SET_DATA_USER] (state, payload) {
    state.data.info = payload
  },
  [types.SET_USER_REGISTRATIONS] (state, registrations) {
    state.registrations = registrations
  },
  [types.SET_USER_DRIVERS] (state, drivers) {
    state.drivers = drivers
  },
  [types.SET_USER_PARTNERS] (state, partners) {
    state.partners = partners
  },
  [types.SET_USER_PROFILES] (state, profiles) {
    state.profiles = profiles
  },
  [types.SET_STORES] (state, stores) {
    state.stores = stores
  },
  [types.SET_FLUSH_USER] (state) {
    state.data = {}
    state.registrations = []
    state.partners = []
    state.profiles = []
    state.stores = []
    state.drivers = []
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/user.js